// src/components/TenantDetailModal.js
import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import PlanLimitsPanel from "./PlanLimitsPanel";
import Chart from "react-apexcharts";
import { FaExpand, FaArrowLeft } from "react-icons/fa";

/**
 * @file TenantDetailModal.js
 * @description Modal for viewing detailed statistics, collaborators, and plans for a specific tenant.
 */
export default function TenantDetailModal({ show, data, onClose }) {
  const [activeTab, setActiveTab] = useState("overview");
  const [expandedChart, setExpandedChart] = useState(null); // 'storage' | 'trend'

  if (!show || !data) return null;

  const info = data.user_info || {};
  const metrics = data.execution_metrics || {};
  const resources = data.resource_counts || {};
  const storage = data.storage_metrics || {};
  const collaborators = data.user_management?.created_users || [];

  // Handle storage metrics safely (API returns milvus/mysql objects)
  const milvus = storage.milvus || { used_mb: 0 };
  const mysql = storage.mysql || { used_mb: 0 };
  const totalStorage = (milvus.used_mb || 0) + (mysql.used_mb || 0);

  const storageSeries = [milvus.used_mb || 0, mysql.used_mb || 0];
  const storageLabels = ["Milvus", "MySQL"];

  const history = data.execution_history || [];
  const trendSeries = [
    { name: "Executions", data: history.map((h) => h.count || h.total_executions || 0) },
  ];
  const trendLabels = history.map((h) => h.date);

  // --- EXPANDED OVERLAY ---
  if (expandedChart) {
    return (
      <Modal
        show={true}
        onHide={() => setExpandedChart(null)}
        size="xl"
        centered
      >
        <div className="p-4 h-100 d-flex flex-column">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <button
              className="btn btn-outline-secondary d-flex align-items-center gap-2"
              onClick={() => setExpandedChart(null)}
            >
              <FaArrowLeft /> Back to Details
            </button>
            <h5 className="fw-bold mb-0">
              {expandedChart === "storage"
                ? "Storage Distribution"
                : "Execution Trend"}
            </h5>
            <div style={{ width: 100 }}></div>
          </div>

          <div className="flex-grow-1 bg-white p-4 rounded shadow-sm d-flex flex-column justify-content-center">
            {expandedChart === "storage" && (
              <Chart
                options={{
                  labels: storageLabels,
                  legend: { position: "right", fontSize: "16px" },
                  colors: ["#AD2D45", "#E68F17", "#912059"],
                  plotOptions: { pie: { donut: { size: "55%" } } },
                }}
                series={storageSeries}
                type="donut"
                height={500}
              />
            )}
            {expandedChart === "trend" && (
              <Chart
                options={{
                  chart: { toolbar: { show: true } },
                  stroke: { curve: "smooth", width: 4 },
                  xaxis: { categories: trendLabels },
                  colors: ["#AD2D45"],
                  dataLabels: { enabled: true },
                }}
                series={trendSeries}
                type="line"
                height={500}
              />
            )}
          </div>
        </div>
      </Modal>
    );
  }

  // --- NORMAL VIEW ---
  const tabs = [
    { id: "overview", label: "Overview", disabled: false },
    { id: "collaborators", label: "Collaborators", disabled: false },
    { id: "plan", label: "Plan & Limits", disabled: true },
    { id: "billing", label: "Billing Summary", disabled: true },
    { id: "audit", label: "Audit & Logs", disabled: true },
  ];

  return (
    <Modal show={show} onHide={onClose} size="xl" centered backdrop="static">
      <div className="p-4 bg-white">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h3 className="fw-bold mb-0">{info.display_name || info.username}</h3>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={onClose}
          >
            Close
          </button>
        </div>

        <div
          className="d-flex gap-2 border bg-light p-1 rounded mb-4"
          style={{ width: "fit-content" }}
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`btn btn-sm ${activeTab === tab.id
                ? "btn-primary shadow-sm"
                : "btn-transparent text-muted"
                } ${tab.disabled ? "opacity-50" : ""}`}
              onClick={() => !tab.disabled && setActiveTab(tab.id)}
              style={{
                minWidth: 100,
                cursor: tab.disabled ? "not-allowed" : "pointer",
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "overview" && (
          <div className="animate-fade-in">
            <div className="row g-3 mb-4">
              <div className="col">
                <div className="border rounded p-3 text-center">
                  <div className="text-muted small">Executions</div>
                  <div className="fw-bold fs-4">{metrics?.executions_used_total}</div>
                </div>
              </div>
              <div className="col">
                <div className="border rounded p-3 text-center">
                  <div className="text-muted small">Workflows</div>
                  <div className="fw-bold fs-4">
                    {resources.total_workflows}
                  </div>
                </div>
              </div>
              <div className="col">
                <div className="border rounded p-3 text-center">
                  <div className="text-muted small">Intent Studio Agents</div>
                  <div className="fw-bold fs-4">{resources.total_agents}</div>
                </div>
              </div>
              <div className="col">
                <div className="border rounded p-3 text-center">
                  <div className="text-muted small">Collaborators</div>
                  <div className="fw-bold fs-4">
                    {data.user_management?.created_users?.length || 0}
                  </div>
                </div>
              </div>
              <div className="col">
                <div className="border rounded p-3 text-center">
                  <div className="text-muted small">Total Storage</div>
                  <div className="fw-bold fs-4">
                    {totalStorage.toFixed(2)} MB
                  </div>
                </div>
              </div>
            </div>

            <div className="row g-4">
              <div className="col-md-5">
                <div className="border rounded p-3 h-100 position-relative">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="fw-bold mb-0">Storage by Type</h6>
                    <button
                      className="btn btn-sm btn-light text-muted"
                      onClick={() => setExpandedChart("storage")}
                    >
                      <FaExpand />
                    </button>
                  </div>
                  <Chart
                    options={{
                      labels: storageLabels,
                      legend: { position: "bottom" },
                      colors: ["#AD2D45", "#E68F17", "#912059"],
                      tooltip: {
                        y: {
                          formatter: (val) => `${val} MB`
                        }
                      }
                    }}
                    series={storageSeries}
                    type="donut"
                    height={250}
                  />
                </div>
              </div>

              <div className="col-md-7">
                <div className="border rounded p-3 h-100 position-relative">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="fw-bold mb-0">Execution Trend</h6>
                    <button
                      className="btn btn-sm btn-light text-muted"
                      onClick={() => setExpandedChart("trend")}
                    >
                      <FaExpand />
                    </button>
                  </div>
                  <Chart
                    options={{
                      chart: { toolbar: { show: false } },
                      stroke: { curve: "smooth" },
                      xaxis: { categories: trendLabels },
                    }}
                    series={trendSeries}
                    type="line"
                    height={250}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "collaborators" && (
          <div>
            <h5 className="fw-bold mb-3">Collaborators</h5>
            <div className="table-responsive border rounded">
              <table className="table table-hover mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Username</th>
                    <th>Role</th>
                    <th>Projects</th>
                  </tr>
                </thead>
                <tbody>
                  {collaborators.length > 0 ? (
                    collaborators.map((user, i) => (
                      <tr key={i}>
                        <td className="fw-medium">
                          {user.display_name || user.username}

                          <span
                            className="badge bg-secondary text-uppercase"
                            style={{ fontSize: 10 }}
                          >
                            {user.role}
                          </span>
                        </td>
                        <td>{user.project_count}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center py-5 text-muted">
                        No collaborators found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "plan" && (
          <PlanLimitsPanel tenant={{ admin_info: info }} />
        )}
      </div>
    </Modal>
  );
}
